import useFormatter from "@/hooks/use-formatter.hook.ts";
import { FeedbackScoreCardProps } from "@/pages/recruit/scorecards/feedback/scorecards-feedback.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useScoreCardFeedbackSaveDraft } from "@/queries/recruit/use-scorecards.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { Avatar, Button, FullScreenPage, ListItem, Stack, Title } from "@skailer-com/uikit";
import { round } from "lodash";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function FeedbackScoreCardStart({ feedback }: FeedbackScoreCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { renderEmployeeName } = useFormatter();
  const startFeedback = useScoreCardFeedbackSaveDraft();
  const { data: employee } = useEmployeeMe();

  const handleStartFeedback = async () => {
    if (!employee) return;
    try {
      await startFeedback.mutateAsync({
        employeeId: employee.id,
        questionnaireId: feedback.id,
        answers: [],
      });

      analyticsService.trackEvent(analyticEvents.scoreCards.feedbackStarted, {
        [analyticProperties.id]: feedback.id,
      });
    } catch {
      // Handle error
    }
  };

  return (
    <FullScreenPage
      title={`${t("scorecards:scorecard")}: ${feedback?.scorecard.title ?? ""}`}
      backSlot={
        <Button
          size={"lg"}
          variant={"secondary"}
          onClick={() => {
            navigate(-1);
          }}
        >
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Button size={"lg"} isLoading={startFeedback.isPending} onClick={handleStartFeedback}>
          {t("common:continue")}
        </Button>
      }
    >
      <Stack className={"card-panel p-6 bg-light/80"} justify={"between"} gap={"xl"}>
        <Avatar
          url={feedback.applicant?.photo_url}
          size={"2xl"}
          variant={"opaque"}
          className={"shadow-lg"}
        />
        <Title
          header={
            feedback.applicant
              ? `${feedback.applicant.first_name} ${feedback.applicant.last_name}`
              : ""
          }
          caption={feedback.scorecard.title}
          size={"lg"}
        />
        <Stack>
          <ListItem
            title={t("common:questions")}
            value={feedback.form_stats.total_questions.toString()}
          />
          <ListItem
            title={t("common:time_to_complete")}
            value={t("plurals:plural_minutes", {
              count: round(feedback.form_stats.total_estimated_time_seconds / 60),
            })}
          />
          <ListItem
            title={t("common:created_by")}
            value={renderEmployeeName(feedback.created_by)}
          />
        </Stack>
        <Stack>
          <Button onClick={handleStartFeedback} size={"lg"} isLoading={startFeedback.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </Stack>
    </FullScreenPage>
  );
}
